// @givmo/board — recording a version of a governing document, or bringing an old one in.
//
// ══ ONE DIALOG, TWO DOORS, AND THE DIFFERENCE IS NOT COSMETIC ══════════════════════════════════
//
// Both doors write the SAME thing — a `board_record` of kind `governing_document` carrying a
// descriptor — through the record core's own routes. There is no governing-document write endpoint,
// deliberately: a second door would be a second answer to "how does something become this organization's
// record". What differs is what the organization is telling us:
//
//   `mode: 'draft'`  → THE BOARD IS ADOPTING THIS NOW. `POST …/records` opens a DRAFT, and a draft is
//                      not a version of anything. It becomes one when an officer approves it, which
//                      happens on the record itself (the minute book), not here — one approval flow,
//                      one place. This dialog says so before it closes, because the registry read
//                      EXCLUDES drafts and an officer who expected to see their work in the chain
//                      would otherwise conclude it was lost.
//   `mode: 'import'` → THIS IS ALREADY THE ORGANIZATION'S HISTORY. `POST …/imported-records` lands it
//                      immutable, with no approver, never routed through submit/approve — nobody here
//                      approved a policy adopted in 2015. A provenance note is REQUIRED, because an
//                      imported record must be able to say where it came from.
//
// ⚠ VERSIONS ARE RECORDED OLDEST FIRST, AND THE BACKEND SAYS SO IN ITS OWN WORDS. Each version names
// the one it replaces, and a partial unique index refuses a second earliest version — so an organization
// importing newest-first gets a 409 on its SECOND record. That is a reachable, expected mistake rather
// than a defect, and the refusal names the fix. The import mode therefore leads with the guidance
// BEFORE the mistake, and every refusal is rendered verbatim: the backend's sentence already says
// which way round to record it, and a rephrasing here could only be a worse copy of it.
//
// ⚠ THE FORM LIBRARY IS BAND 2 AND ITS RAILS ARE STRUCTURAL HERE, exactly as in the consent dialog:
// organization-SELECTED (the officer opens a list and picks; nothing reads the organization's facts and
// proposes one), organization-POPULATED (every operative term is a `[bracketed]` field the officer types —
// this app supplies no value for any of them), and LABELLED FOR COUNSEL wherever a form is shown.
// There is NO BYLAWS FORM, by design, and when the library ships none for the chosen instrument this
// dialog renders no forms section at all rather than explaining the absence — an explanation would be
// product voice about what an organization's instruments ought to look like.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardGoverningDocumentDescriptor,
  type OrgBoardGoverningDocumentOut,
  type BoardGoverningDocumentTemplateOut,
  type OrgBoardRecordOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Select,
  Skeleton,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { TemplateNotice } from '../consents/template-notice';
import {
  fillTemplateBody,
  isLongFormField,
  remainingFields,
  templateFieldLabel,
  type TemplateFieldValues,
} from '../consents/template-fill';
import { isTheRecord, recordKindLabel } from '../records/records-decode';
import { displayDate } from '../../lib/display';
import {
  DOCUMENT_KINDS,
  documentKindLabel,
  documentTitle,
  extractGoverningDocumentTemplates,
  requiresLabel,
  templatesForKind,
  versionInForce,
} from './govdocs-decode';
import { useBoardOperations } from '../../board-runtime';

/** The record kinds the backend accepts as the act that adopted a version (`ADOPTING_RECORD_KINDS`). */
const ADOPTING_RECORD_KINDS: readonly string[] = ['minutes', 'resolution', 'written_consent'];

export type VersionDialogMode = 'draft' | 'import';

export interface VersionDialogProps {
  orgRef: string;
  /** The door the dialog opens on. */
  mode: VersionDialogMode;
  /**
   * The doors this person may choose between (CBG-UX1). "Add a document" passes both when both
   * strings are held — drafting (`board_record:write`) and importing (`board_record:import`) — and
   * the dialog then opens with the draft-or-import choice at its top, because the part no longer has
   * a separate import button. Omitted, the dialog is the one door it was opened on.
   */
  modes?: readonly VersionDialogMode[];
  /** The registry as read, so the dialog can offer the version a new one replaces. */
  documents: readonly OrgBoardGoverningDocumentOut[];
  /** Pre-selected instrument when opened from a document's own card. */
  initialKind?: string;
  initialLabel?: string;
  /**
   * CBG-YR6-REG: a form to open on arrival — a committee added from a form drafts its charter from
   * that form (`board-committees.md` §4.1). The person still fills every field and saves.
   */
  initialTemplateId?: string;
  onClose: () => void;
  onSaved: (record: OrgBoardRecordOut) => void;
}

export function VersionDialog({
  orgRef,
  mode: openedOn,
  modes = [openedOn],
  documents,
  initialKind,
  initialLabel,
  initialTemplateId,
  onClose,
  onSaved,
}: VersionDialogProps) {
  const operations = useBoardOperations();
  const [mode, setMode] = useState<VersionDialogMode>(openedOn);
  const offersChoice = modes.includes('draft') && modes.includes('import');
  // "Add a document" (the opener named no instrument) is for an instrument the registry does not
  // hold yet. A one-per-board instrument with a recorded version already IS a document — adding to
  // it is that document's own "Add a version" — so it is not on offer here. The kinds a board holds
  // several of (committee charters, "another instrument") stay on offer: a new name is a new
  // document. So when every one-per-board instrument is recorded, those two are what it offers.
  const addingDocument = initialKind === undefined;
  const recordedKinds = new Set(documents.map((entry) => entry.document_kind));
  const offeredKinds = addingDocument
    ? DOCUMENT_KINDS.filter(
        (entry) => requiresLabel(entry.value) || !recordedKinds.has(entry.value),
      )
    : DOCUMENT_KINDS;
  const [kind, setKind] = useState<string>(
    () => initialKind ?? offeredKinds[0]?.value ?? 'committee_charter',
  );
  const [label, setLabel] = useState<string>(initialLabel ?? '');
  const [title, setTitle] = useState('');
  // Empty, never today: an imported version was adopted before Givmo, and a draft's adoption date is
  // the approval's, which the approval dialog collects (CBG-FIX6). Neither door requires one.
  const [adoptedOn, setAdoptedOn] = useState('');
  const [typedBody, setTypedBody] = useState('');
  const [template, setTemplate] = useState<BoardGoverningDocumentTemplateOut | null>(null);
  const [values, setValues] = useState<TemplateFieldValues>({});
  const [sourceNote, setSourceNote] = useState('');
  const [artifactRef, setArtifactRef] = useState('');
  const [adoptedByRecordId, setAdoptedByRecordId] = useState<string>('');
  const [supersedesRecordId, setSupersedesRecordId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const needsLabel = requiresLabel(kind);

  // The chain this version would join, if the organization has already recorded one. Matched on the same
  // key the backend partitions on: kind alone, plus the label for the kinds an organization holds several of.
  const existing = useMemo(
    () =>
      documents.find(
        (entry) =>
          entry.document_kind === kind &&
          (needsLabel ? (entry.label ?? '').trim() === label.trim() : true),
      ) ?? null,
    [documents, kind, label, needsLabel],
  );
  const inForce = existing === null ? null : versionInForce(existing);
  // "Add a document" with a name the organization has already recorded: that document exists, and a
  // version of it goes through its own card — so this door says so and records nothing.
  const nameTaken = addingDocument && existing !== null;

  const templatesQuery = useQuery({
    queryKey: ['board-governing-document-templates', orgRef],
    queryFn: async (): Promise<BoardGoverningDocumentTemplateOut[]> => {
      const { data } = await operations.officerListBoardGoverningDocumentTemplates({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return extractGoverningDocumentTemplates(data);
    },
  });

  // The 100 most recent records this reader may see. The list is newest-first, and the act that
  // adopted a version the officer is recording today is a recent one — so this window is the picker's
  // whole population in practice, and a record outside it is one nobody is about to cite.
  const adoptingQuery = useQuery({
    queryKey: ['board-records', orgRef, 'adopting'],
    queryFn: async (): Promise<OrgBoardRecordOut[]> => {
      const { data } = await operations.officerListBoardRecords({
        path: { org_ref: orgRef },
        query: { 'page[limit]': 100 },
        throwOnError: true,
      });
      const rows = Array.isArray(data)
        ? data
        : ((data as { data?: OrgBoardRecordOut[] })?.data ?? []);
      return rows.filter(
        (record) => isTheRecord(record) && ADOPTING_RECORD_KINDS.includes(record.record_kind),
      );
    },
  });

  const forms = templatesForKind(templatesQuery.data ?? [], kind);
  const fields = template?.fill_in_fields ?? [];
  const body = useMemo(() => {
    if (template === null) return typedBody;
    return fillTemplateBody(template.body ?? '', fields, values);
  }, [template, fields, values, typedBody]);
  const stillToFill = useMemo(
    () => (template === null ? [] : remainingFields(body, fields)),
    [template, body, fields],
  );

  const openForm = useMutation({
    mutationFn: async (templateId: string): Promise<BoardGoverningDocumentTemplateOut> => {
      // The list projection withholds the body; the full text comes from the form's own route.
      const { data } = await operations.officerGetBoardGoverningDocumentTemplate({
        path: { org_ref: orgRef, template_id: templateId },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data) => {
      setTemplate(data);
      setValues({});
      if (title.trim().length === 0) setTitle(data.name ?? '');
      setError(null);
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t open that form just now.'),
  });

  // CBG-YR6-REG: the form a committee was added from, opened once on arrival.
  const openedInitialForm = useRef(false);
  const openFormNow = openForm.mutate;
  useEffect(() => {
    if (initialTemplateId === undefined || openedInitialForm.current) return;
    openedInitialForm.current = true;
    openFormNow(initialTemplateId);
  }, [initialTemplateId, openFormNow]);

  // `document_kind` is a closed enum on the wire and an open string in this component's state — the
  // picker is built from DOCUMENT_KINDS, which mirrors that enum, so the narrowing is safe and the
  // backend refuses anything else regardless.
  const descriptor = (): BoardGoverningDocumentDescriptor => ({
    document_kind: kind as BoardGoverningDocumentDescriptor['document_kind'],
    ...(needsLabel ? { label: label.trim() } : {}),
    ...(supersedesRecordId.length > 0 ? { supersedes_record_id: supersedesRecordId } : {}),
    ...(adoptedByRecordId.length > 0 ? { adopted_by_record_id: adoptedByRecordId } : {}),
  });

  const save = useMutation({
    mutationFn: async (): Promise<OrgBoardRecordOut> => {
      const shared = {
        record_kind: 'governing_document' as const,
        title: title.trim(),
        body,
        ...(adoptedOn.length > 0 ? { occurred_on: adoptedOn } : {}),
        ...(artifactRef.trim().length > 0 ? { artifact_ref: artifactRef.trim() } : {}),
        governing_document: descriptor(),
      };
      if (mode === 'import') {
        const { data } = await operations.officerImportBoardRecord({
          path: { org_ref: orgRef },
          body: { ...shared, source_note: sourceNote.trim() },
          throwOnError: true,
        });
        return data;
      }
      const { data } = await operations.officerCreateBoardRecordDraft({
        path: { org_ref: orgRef },
        body: shared,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (record) => {
      toast.success(
        mode === 'import'
          ? 'Recorded in your minute book.'
          : 'Saved as a draft. It becomes a version of this document once it is approved.',
      );
      onSaved(record);
    },
    // Rendered VERBATIM. The backend's refusals here already say what to do — "a document's versions
    // are recorded oldest first", "record this version as replacing the version now in force" — and
    // a rephrasing could only be a worse copy of a sentence written for exactly this moment.
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      ),
  });

  const canSave =
    title.trim().length > 0 &&
    (!needsLabel || label.trim().length > 0) &&
    (mode !== 'import' || sourceNote.trim().length > 0) &&
    !nameTaken &&
    !save.isPending;

  // The heading names the button that opened it (Jonathan, 2026-09-28): a document's own "Add a
  // version" pre-selects that document's instrument; the page's "Add a document" does not, and keeps
  // its name whichever of its two doors is chosen.
  const heading =
    initialKind === undefined
      ? 'Add a document'
      : mode === 'import'
        ? 'Import an earlier version'
        : 'Add a version';

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={heading}
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="govdoc-version-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {heading}
        </Dialog.Title>

        {offersChoice ? (
          <RadioTileGroup
            value={mode}
            onValueChange={(next) => setMode(next === 'import' ? 'import' : 'draft')}
            aria-label="Draft it or import it"
            className="gap-10"
            testId="govdoc-mode"
          >
            <RadioTile
              value="draft"
              label="Draft it here"
              description="It joins your governing documents once approved."
              testId="govdoc-mode-draft"
            />
            <RadioTile
              value="import"
              label="Import one adopted before Givmo"
              description="It’s recorded as it stands, with where it came from."
              testId="govdoc-mode-import"
            />
          </RadioTileGroup>
        ) : null}

        {mode === 'import' ? (
          <FieldNote testId="govdoc-oldest-first">
            Record a document’s versions oldest first. Each one names the version it replaces, so
            the earlier one has to be in your minute book already.
          </FieldNote>
        ) : !offersChoice ? (
          // Only where no choice is on screen: the choice's own line already says it.
          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="govdoc-draft-note"
          >
            This is saved as a draft. It becomes a version of this document when an officer approves
            it — until then it sits in your minute book and is not part of the document’s history.
          </p>
        ) : null}

        <Select
          label="Which instrument"
          appearance="saas"
          value={kind}
          disabled={initialKind !== undefined}
          onValueChange={(next) => {
            if (next === null) return;
            setKind(next);
            setTemplate(null);
            setValues({});
            setSupersedesRecordId('');
          }}
          options={offeredKinds.map((entry) => ({ value: entry.value, label: entry.label }))}
          testId="govdoc-kind"
        />

        {needsLabel ? (
          <>
            <TextField
              label="Which one"
              value={label}
              maxLength={200}
              disabled={initialLabel !== undefined}
              onChange={(event) => setLabel(event.target.value)}
              testId="govdoc-label"
            />
            <FieldNote>
              A board holds several of these, so each one is kept under its own name — “Finance
              committee”, for example.
            </FieldNote>
          </>
        ) : null}

        {nameTaken && existing !== null ? (
          <FieldNote testId="govdoc-name-taken">
            You have already recorded {documentTitle(existing)}. To add a version of it, use “Add a
            version” on its card.
          </FieldNote>
        ) : null}

        {/* ── Which version this replaces (a document's own "Add a version" only) ───────────── */}
        {!addingDocument && existing !== null && inForce !== null ? (
          <div className="flex flex-col gap-8" data-testid="govdoc-supersedes">
            <Select
              label="Does this replace a version you have already recorded?"
              appearance="saas"
              value={supersedesRecordId}
              onValueChange={(next) => setSupersedesRecordId(next ?? '')}
              options={[
                { value: '', label: 'No — this is an earlier one' },
                {
                  value: inForce.record_id,
                  label: `Replaces ${(inForce.record?.title ?? '').trim() || documentTitle(existing)}`,
                },
              ]}
              testId="govdoc-supersedes-select"
            />
            <FieldNote>
              You have already recorded {existing.version_count}{' '}
              {existing.version_count === 1 ? 'version' : 'versions'} of this document. The one
              standing is {(inForce.record?.title ?? '').trim() || 'the most recent'}
              {inForce.adopted_on ? `, adopted ${displayDate(inForce.adopted_on)}` : ''}.
            </FieldNote>
          </div>
        ) : null}

        {/* ── The form library ───────────────────────────────────────────────────────────────── */}
        {templatesQuery.isPending ? (
          <Skeleton className="h-[60px] w-full rounded-lg" testId="govdoc-templates-loading" />
        ) : null}

        {template === null && forms.length > 0 ? (
          <div className="flex flex-col gap-10" data-testid="govdoc-templates">
            <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
              Start from a form, or write it yourself
            </span>
            {forms.map((form) => (
              <Card key={form.template_id} testId={`govdoc-template-${form.template_id}`}>
                <CardBody className="flex flex-col gap-8">
                  <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                    {form.name}
                  </span>
                  <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                    {form.provenance}
                  </span>
                  <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                    {form.review_line}
                  </span>
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    className="self-start"
                    loading={openForm.isPending && openForm.variables === form.template_id}
                    disabled={openForm.isPending}
                    onClick={() => openForm.mutate(form.template_id)}
                    testId={`govdoc-template-use-${form.template_id}`}
                  >
                    Use this form
                  </Button>
                </CardBody>
              </Card>
            ))}
          </div>
        ) : null}

        {template !== null ? (
          <TemplateNotice
            reviewLine={template.review_line}
            provenance={template.provenance}
            sourceCitations={template.source_citations}
            testId="govdoc-template-notice"
          />
        ) : null}

        {/* ── What it says ───────────────────────────────────────────────────────────────────── */}
        <TextField
          label="Title"
          value={title}
          maxLength={300}
          onChange={(event) => setTitle(event.target.value)}
          testId="govdoc-title"
        />

        <TextField
          label={mode === 'import' ? 'Date your board adopted it' : 'Date of adoption'}
          type="date"
          value={adoptedOn}
          onChange={(event) => setAdoptedOn(event.target.value)}
          testId="govdoc-adopted-on"
        />
        <FieldNote testId="govdoc-adopted-on-note">
          {mode === 'import'
            ? 'The date your board adopted this version. It is also what your record is kept from.'
            : 'The date your board adopted this version, if it already has. Otherwise leave it empty: the date is given when the draft is approved.'}
        </FieldNote>

        {template !== null ? (
          <>
            {fields.map((field) => (
              <FormField
                key={field}
                field={field}
                value={values[field] ?? ''}
                onChange={(next) => setValues((current) => ({ ...current, [field]: next }))}
              />
            ))}
            {stillToFill.length > 0 ? (
              <FieldNote testId="govdoc-remaining">
                {stillToFill.length} {stillToFill.length === 1 ? 'field is' : 'fields are'} still in
                brackets below.
              </FieldNote>
            ) : null}
            <div className="flex flex-col gap-6">
              <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                What this version will say
              </span>
              <pre
                className="max-h-[280px] overflow-y-auto whitespace-pre-wrap rounded-md border border-border-subtle bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                data-testid="govdoc-preview"
              >
                {body}
              </pre>
            </div>
          </>
        ) : (
          <Textarea
            label="What this version says"
            rows={12}
            value={typedBody}
            onChange={(event) => setTypedBody(event.target.value)}
            testId="govdoc-body"
          />
        )}

        {/* ── Where it came from, and what it points at ──────────────────────────────────────── */}
        {mode === 'import' ? (
          <>
            <TextField
              label="Where this came from"
              value={sourceNote}
              maxLength={2000}
              onChange={(event) => setSourceNote(event.target.value)}
              testId="govdoc-source-note"
            />
            <FieldNote>
              Your own account of where this document came from — “minute book, page 14”. It is kept
              with the record so a reader can always tell it apart from one written here.
            </FieldNote>
          </>
        ) : null}

        <Select
          label="The record of the act that adopted it (optional)"
          appearance="saas"
          value={adoptedByRecordId}
          onValueChange={(next) => setAdoptedByRecordId(next ?? '')}
          options={[
            { value: '', label: 'Not recorded' },
            ...(adoptingQuery.data ?? []).map((record) => ({
              value: record.id,
              label: `${recordKindLabel(record.record_kind)} — ${record.title}`,
            })),
          ]}
          testId="govdoc-adopted-by"
        />
        <FieldNote>
          The minutes, resolution or written consent your board adopted this in, if it is in your
          minute book.
        </FieldNote>

        <TextField
          label="Reference to your own copy of the document (optional)"
          value={artifactRef}
          maxLength={512}
          onChange={(event) => setArtifactRef(event.target.value)}
          testId="govdoc-artifact-ref"
        />
        <FieldNote>
          A reference to where the signed original is kept in your own storage. Givmo records the
          reference; the file itself stays where it is.
        </FieldNote>

        {error !== null ? <ErrorMessage testId="govdoc-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="govdoc-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="govdoc-save"
          >
            {mode === 'import' ? 'Record it' : 'Save as a draft'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function FormField({
  field,
  value,
  onChange,
}: {
  field: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const label = templateFieldLabel(field);
  const testId = `govdoc-field-${slug(field)}`;
  return isLongFormField(field) ? (
    <Textarea
      label={label}
      rows={4}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      testId={testId}
    />
  ) : (
    <TextField
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      testId={testId}
    />
  );
}

/** A stable testid fragment for a field name — the data contract e2e selects on. */
function slug(field: string): string {
  return templateFieldLabel(field)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

/** Re-exported so a caller can label its own entry point with the same words. */
export { documentKindLabel };
